import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import * as dashboardApi from "../api/dashboard";
import "./AdminDashboard.css";
import AppTooltip from "./AppTooltip";

const formatPkr = (value) => {
  const num = Number(value) || 0;
  return `Rs ${num.toLocaleString("en-PK", { maximumFractionDigits: 0 })}`;
};

const formatCompact = (value) => {
  const num = Number(value) || 0;
  if (num >= 100000) return `${(num / 1000).toFixed(0)}k`;
  if (num >= 10000) return `${(num / 1000).toFixed(1)}k`;
  return num.toLocaleString("en-PK");
};

const hourGreeting = (name) => {
  const hour = new Date().getHours();
  const who = name || "there";
  if (hour < 12) return `Morning, ${who}`;
  if (hour < 17) return `Afternoon, ${who}`;
  return `Evening, ${who}`;
};

const reflectiveLine = () => {
  const hour = new Date().getHours();
  if (hour < 12) return "The ledger missed you — shall we see what’s waiting?";
  if (hour < 17) return "Shall we tidy the queues before the day runs away?";
  return "One last glance before lights out.";
};

const buildBriefing = (summary) => {
  if (!summary) return "";
  const bits = [];
  if (summary.registrations?.newLast48h > 0) {
    bits.push(
      `${summary.registrations.newLast48h} new enrollment${
        summary.registrations.newLast48h === 1 ? "" : "s"
      } in 48h`,
    );
  }
  if (summary.registrations?.pendingFeeProofs > 0) {
    bits.push(
      `${summary.registrations.pendingFeeProofs} fee proof${
        summary.registrations.pendingFeeProofs === 1 ? "" : "s"
      } to verify`,
    );
  }
  if (summary.registrations?.overdueCount > 0) {
    bits.push(
      `${summary.registrations.overdueCount} overdue balance${
        summary.registrations.overdueCount === 1 ? "" : "s"
      }`,
    );
  }
  if (summary.courses?.inactiveWithEnrollments > 0) {
    bits.push(
      `${summary.courses.inactiveWithEnrollments} inactive course${
        summary.courses.inactiveWithEnrollments === 1 ? "" : "s"
      } still enrolled`,
    );
  }
  if (!bits.length) return "Nothing urgent needs you right now.";
  return bits.join(" · ");
};

const buildActionQueue = (summary) => {
  const items = [];
  if (summary?.registrations) {
    if (summary.registrations.pendingFeeProofs > 0) {
      items.push({
        key: "proofs",
        title: "Fee proofs to verify",
        count: summary.registrations.pendingFeeProofs,
        to: "/admin/registrations",
      });
    }
    if (summary.registrations.overdueCount > 0) {
      items.push({
        key: "overdue",
        title: "Overdue balances",
        count: summary.registrations.overdueCount,
        to: "/admin/registrations",
      });
    }
    if (summary.registrations.newLast48h > 0) {
      items.push({
        key: "new",
        title: "New enrollments",
        count: summary.registrations.newLast48h,
        to: "/admin/registrations",
      });
    }
  }
  if (summary?.financialAid?.drafts > 0) {
    items.push({
      key: "aid-drafts",
      title: "Aid drafts",
      count: summary.financialAid.drafts,
      to: "/admin/financial-aid",
    });
  }
  if (summary?.cashLedger?.recentReversalsOrRefunds > 0) {
    items.push({
      key: "reversals",
      title: "Reversals / refunds",
      count: summary.cashLedger.recentReversalsOrRefunds,
      to: "/admin/cash-ledger",
    });
  }
  if (summary?.courses?.inactiveWithEnrollments > 0) {
    items.push({
      key: "inactive-courses",
      title: "Inactive courses with enrollments",
      count: summary.courses.inactiveWithEnrollments,
      to: "/admin/courses",
    });
  }
  return items;
};

/** Fixed 8-slot KPI set; unauthorized sections render as empty pads. */
const buildKpis = (summary) => {
  const slot = (key, filled) =>
    filled || { key, empty: true, label: "—", value: "—" };

  return [
    slot(
      "enrollments",
      summary?.registrations && {
        key: "enrollments",
        label: "Enrolled course links",
        value: formatCompact(summary.registrations.activeEnrollments),
        to: "/admin/registrations",
      },
    ),
    slot(
      "outstanding",
      summary?.registrations && {
        key: "outstanding",
        label: "Unpaid fee balance",
        value: formatPkr(summary.registrations.outstandingBalance),
        to: "/admin/registrations",
      },
    ),
    slot(
      "proofs",
      summary?.registrations && {
        key: "proofs",
        label: "Fee proofs waiting review",
        value: formatCompact(summary.registrations.pendingFeeProofs),
        to: "/admin/registrations",
      },
    ),
    slot(
      "collected",
      summary?.cashLedger && {
        key: "collected",
        label: "30d net cash",
        value: formatPkr(
          summary.cashLedger.collectedLast30Days ??
            summary.cashLedger.collectedThisMonth,
        ),
        to: "/admin/cash-ledger",
      },
    ),
    slot(
      "aid",
      summary?.financialAid && {
        key: "aid",
        label: "Unattended FinAid drafts",
        value: formatCompact(summary.financialAid.drafts),
        to: "/admin/financial-aid",
      },
    ),
    slot(
      "courses",
      summary?.courses && {
        key: "courses",
        label: "Active courses",
        value: formatCompact(summary.courses.activeCount),
        to: "/admin/courses",
      },
    ),
    slot(
      "users",
      summary?.users && {
        key: "users",
        label: "Student accounts",
        value: formatCompact(summary.users.total),
        to: "/admin/users",
      },
    ),
    slot(
      "teachers",
      summary?.teachers && {
        key: "teachers",
        label: "Teachers",
        value: formatCompact(summary.teachers.count),
        to: "/admin/teachers",
      },
    ),
  ];
};

const seriesTotal = (series) =>
  (Array.isArray(series) ? series : []).reduce(
    (sum, d) => sum + (Number(d.value) || 0),
    0,
  );

const formatChartDay = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(`${String(dateStr).slice(0, 10)}T12:00:00`);
  if (Number.isNaN(d.getTime())) return String(dateStr).slice(5);
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
};

function ChartFoot({ text, value }) {
  return (
    <p className="ops-chart-foot">
      <strong>{value}</strong>
      <span>{text}</span>
    </p>
  );
}

function MiniBars({
  series,
  variant = "default",
  valueFormatter = formatCompact,
  days = 7,
  unitLabel = "collected",
}) {
  const [hover, setHover] = useState(null);
  const values = Array.isArray(series) ? series : [];
  const recent = values.slice(-days);
  const isCash = variant === "cash";
  // Cash series `value` is already receipts − reversals/refunds (net only on chart).
  const total = seriesTotal(recent);
  const max = Math.max(
    ...recent.map((d) => Math.max(0, Number(d.value) || 0)),
    1,
  );
  const allFlat = total <= 0;
  const n = recent.length;
  const mid = n > 2 ? Math.floor((n - 1) / 2) : -1;
  const labelIndexes = new Set(
    [0, mid, n - 1].filter((i) => i >= 0 && i < n),
  );

  const axisLabels = [...labelIndexes]
    .sort((a, b) => a - b)
    .map((index) => ({
      index,
      text: formatChartDay(recent[index]?.date),
      align: index === 0 ? "start" : index === n - 1 ? "end" : "center",
    }));

  return (
    <div className="ops-chart-body">
      <div
        className={`ops-bars${allFlat ? " is-empty" : ""}`}
        onMouseLeave={() => setHover(null)}
      >
        {hover != null && recent[hover] ? (
          <div
            className={`ops-bar-tip ops-bar-tip--${
              hover <= 2 ? "start" : hover >= n - 3 ? "end" : "center"
            }`}
            style={{ left: `${((hover + 0.5) / n) * 100}%` }}
            role="tooltip"
          >
            <span className="ops-bar-tip-date">
              {formatChartDay(recent[hover].date)}
            </span>
            <span className="ops-bar-tip-line">
              <span>
                {isCash
                  ? "Net collected"
                  : (() => {
                      const count = Number(recent[hover].value) || 0;
                      const word =
                        count === 1 && unitLabel.endsWith("s")
                          ? unitLabel.slice(0, -1)
                          : unitLabel;
                      return word.charAt(0).toUpperCase() + word.slice(1);
                    })()}
              </span>
              <strong>
                {valueFormatter(Number(recent[hover].value) || 0)}
              </strong>
            </span>
          </div>
        ) : null}
        {recent.map((point, index) => {
          const net = Math.max(0, Number(point.value) || 0);
          const height = allFlat ? 12 : Math.max(8, (net / max) * 100);

          return (
            <div
              key={point.date || index}
              className={`ops-bar-col${hover === index ? " is-hot" : ""}`}
              onMouseEnter={() => setHover(index)}
            >
              <div
                className={`ops-bar ops-bar--${variant}${allFlat ? " is-flat" : ""}`}
                style={{ height: `${height}%` }}
              />
            </div>
          );
        })}
      </div>
      {axisLabels.length ? (
        <div className="ops-bars-axis" aria-hidden="true">
          {axisLabels.map((label) => (
            <span
              key={`${label.index}-${label.text}`}
              className={`ops-bars-axis-label ops-bars-axis-label--${label.align}`}
            >
              {label.text}
            </span>
          ))}
        </div>
      ) : null}
      {allFlat ? (
        <p className="ops-chart-empty">
          No {unitLabel} in the last {days} days.
        </p>
      ) : (
        <ChartFoot
          text={`${isCash ? "net " : ""}${unitLabel} in ${days} days`}
          value={valueFormatter(total)}
        />
      )}
    </div>
  );
}

const PIE_COLORS = [
  "#5bb894",
  "#4a9bb8",
  "#e0a04a",
  "#d47878",
  "#6a8ec8",
  "#8bc49a",
  "#c48a6a",
  "#7aa87a",
  "#b88a9a",
  "#5aaeae",
  "#d4b06a",
  "#6bb89a",
  "#9a7ab8",
  "#b87a7a",
  "#6a9ec8",
  "#a8c46a",
  "#c49a6a",
  "#5a9e8a",
  "#d48a6a",
  "#7a9ab8",
];

function buildTeacherDonutArcs(rows, valueKey) {
  const active = rows.filter((row) => Number(row[valueKey]) > 0);
  const total = active.reduce((sum, row) => sum + Number(row[valueKey]), 0);
  const size = 120;
  const cx = size / 2;
  const cy = size / 2;
  const radius = 42;
  const stroke = 16;
  const circumference = 2 * Math.PI * radius;
  const gap = active.length > 1 ? 1.6 : 0;
  let cursor = 0;
  const arcs = active.map((row) => {
    const value = Number(row[valueKey]) || 0;
    const raw = total > 0 ? (value / total) * circumference : 0;
    const length = Math.max(0, raw - gap);
    const arc = { ...row, value, length, offset: -cursor };
    cursor += raw;
    return arc;
  });
  return { arcs, total, size, cx, cy, radius, stroke, circumference };
}

function TeacherLoadDonut({
  arcs,
  total,
  size,
  cx,
  cy,
  radius,
  stroke,
  circumference,
  centerLabel,
  ariaLabel,
  activeId,
  onActivate,
}) {
  const linked = arcs.some((arc) => arc.teacherId === activeId);
  return (
    <div
      className={`ops-teacher-pie-viz${linked ? " is-linked" : ""}`}
      role="img"
      aria-label={ariaLabel}
    >
      <svg
        viewBox={`0 0 ${size} ${size}`}
        className="ops-teacher-pie-svg"
        preserveAspectRatio="xMidYMid meet"
      >
        <g transform={`rotate(-90 ${cx} ${cy})`}>
          {total <= 0 ? (
            <circle
              cx={cx}
              cy={cy}
              r={radius}
              fill="none"
              stroke="rgba(226, 230, 242, 0.95)"
              strokeWidth={stroke}
            />
          ) : (
            arcs.map((arc) => (
              <circle
                key={arc.teacherId}
                className={`ops-teacher-arc${
                  arc.teacherId === activeId ? " is-active" : ""
                }`}
                cx={cx}
                cy={cy}
                r={radius}
                fill="none"
                stroke={arc.color}
                strokeWidth={stroke}
                strokeDasharray={`${arc.length} ${circumference - arc.length}`}
                strokeDashoffset={arc.offset}
                onMouseEnter={() => onActivate?.(arc.teacherId)}
                onMouseLeave={() => onActivate?.(null)}
              />
            ))
          )}
        </g>
      </svg>
      <div className="ops-teacher-pie-center">
        <strong>{formatCompact(total)}</strong>
        <span>{centerLabel}</span>
      </div>
    </div>
  );
}

function TeacherEnrollmentPie({ teachers, schedule }) {
  const [activeId, setActiveId] = useState(null);
  const rows = Array.isArray(teachers) ? teachers : [];
  if (!rows.length) {
    return <p className="ops-muted">No teachers yet.</p>;
  }

  const ranked = [...rows]
    .map((teacher) => ({
      teacherId: teacher.teacherId,
      name: teacher.name,
      students: Number(teacher.students) || 0,
      courses: Number(teacher.courses) || 0,
    }))
    .sort(
      (a, b) =>
        b.students - a.students ||
        b.courses - a.courses ||
        String(a.name).localeCompare(String(b.name)),
    )
    .map((teacher, index) => ({
      ...teacher,
      color:
        teacher.students > 0 || teacher.courses > 0
          ? PIE_COLORS[index % PIE_COLORS.length]
          : "#c5cad6",
    }));

  const studentDonut = buildTeacherDonutArcs(ranked, "students");
  const courseDonut = buildTeacherDonutArcs(ranked, "courses");
  const maxStudents = Math.max(...ranked.map((row) => row.students), 1);
  const chips = schedule
    ? [
        {
          key: "day",
          label: "Busiest day",
          value: schedule.busiestDay,
          detail: schedule.busiestDayHours,
        },
        schedule.peakSlot
          ? {
              key: "slot",
              label: "Peak slot",
              value: schedule.peakSlot,
              detail:
                schedule.peakClasses > 1
                  ? `${schedule.peakClasses} classes`
                  : "1 class",
            }
          : null,
        {
          key: "run",
          label: "Longest run",
          value: schedule.longestRunHours,
          detail: `${schedule.longestRunDay} · ${schedule.longestRunSlot}`,
        },
      ].filter(Boolean)
    : [];

  return (
    <div className="ops-enroll-block ops-enroll-block--pie">
      <div className="ops-enroll-hero">
        <div className="ops-enroll-hero-copy">
          <div className="ops-enroll-hero-title">
            <h2>Teaching load</h2>
            <AppTooltip
              wrap
              plain
              onlyWhenTruncated={false}
              placement="bottom"
              label="Counts distinct enrolled students on each teacher's active courses. A student enrolled in multiple courses with the same teacher is counted once."
            >
              <button
                type="button"
                className="ops-help-tip"
                aria-label="About teaching load counts"
              >
                i
              </button>
            </AppTooltip>
          </div>
          {chips.length ? (
            <div className="ops-schedule-chips">
              {chips.map((chip) => (
                <div key={chip.key} className="ops-schedule-chip">
                  <span>{chip.label}</span>
                  <strong>{chip.value}</strong>
                  <em>{chip.detail}</em>
                </div>
              ))}
            </div>
          ) : (
            <p className="ops-muted">No class times on active courses yet.</p>
          )}
        </div>
        <div className="ops-teacher-pie-pair">
          <TeacherLoadDonut
            {...studentDonut}
            centerLabel="students"
            ariaLabel="Students per teacher on active courses"
            activeId={activeId}
            onActivate={setActiveId}
          />
          <TeacherLoadDonut
            {...courseDonut}
            centerLabel="courses"
            ariaLabel="Active courses per teacher"
            activeId={activeId}
            onActivate={setActiveId}
          />
        </div>
      </div>
      <div className="ops-teacher-pie-key">
        {ranked.map((row) => {
          const barWidth =
            row.students > 0
              ? Math.max(8, Math.round((row.students / maxStudents) * 100))
              : 0;
          const idle = row.students <= 0 && row.courses <= 0;
          return (
            <div
              key={row.teacherId}
              className={`ops-teacher-pie-row${idle ? " is-idle" : ""}${
                row.teacherId === activeId ? " is-active" : ""
              }`}
              onMouseEnter={() => setActiveId(row.teacherId)}
              onMouseLeave={() => setActiveId(null)}
            >
              <span className="ops-teacher-pie-id">
                <span
                  className="ops-teacher-pie-swatch"
                  style={{ background: row.color }}
                />
                <span className="ops-teacher-pie-name">{row.name}</span>
              </span>
              <span className="ops-teacher-pie-bar" aria-hidden="true">
                <span
                  style={{
                    width: `${barWidth}%`,
                    background: row.color,
                  }}
                />
              </span>
              <span className="ops-teacher-pie-metrics">
                <strong>
                  {row.students} student{row.students === 1 ? "" : "s"}
                </strong>
                <span>
                  {row.courses} course{row.courses === 1 ? "" : "s"}
                </span>
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TopLoadList({ items, emptyText = "Nothing to show yet." }) {
  const rows = Array.isArray(items) ? items.slice(0, 5) : [];
  if (!rows.length) {
    return <p className="ops-muted">{emptyText}</p>;
  }
  return (
    <div className="ops-top-list">
      {rows.map((item) => {
        const students = Number(item.students) || 0;
        return (
          <AppTooltip
            key={item.id}
            label={item.name}
            wrap
            className="app-tip--stretch"
          >
            <div className="ops-top-list-row">
              <span className="ops-top-list-name">{item.name}</span>
              <span className="ops-top-list-count">
                {students} student{students === 1 ? "" : "s"}
              </span>
            </div>
          </AppTooltip>
        );
      })}
    </div>
  );
}

const CONTENT_MIX_COLORS = {
  all: "#5bb894",
  lecture_notes: "#6a8ec8",
  lecture_worksheet: "#4a9bb8",
  notes_worksheet: "#8bc49a",
  lecture_only: "#e0a04a",
  notes_only: "#c48a6a",
  worksheet_only: "#9a7ab8",
  none: "#b0b6c6",
};

function ContentMixPie({ mix, totalTopics }) {
  const [activeKey, setActiveKey] = useState(null);
  const rows = Array.isArray(mix) ? mix : [];
  const total =
    Number(totalTopics) ||
    rows.reduce((sum, row) => sum + (Number(row.count) || 0), 0);
  const size = 120;
  const cx = size / 2;
  const cy = size / 2;
  const r = 44;
  const stroke = 18;
  const circumference = 2 * Math.PI * r;

  const slices = rows.map((row) => ({
    ...row,
    count: Number(row.count) || 0,
    color: CONTENT_MIX_COLORS[row.key] || "#b0b6c6",
  }));

  const active = slices.filter((s) => s.count > 0);
  const linked = active.some((slice) => slice.key === activeKey);
  let dashOffset = 0;
  const rings =
    total <= 0 || active.length === 0 ? (
      <circle
        cx={cx}
        cy={cy}
        r={r}
        fill="none"
        stroke="rgba(226, 230, 242, 0.95)"
        strokeWidth={stroke}
      />
    ) : (
      active.map((slice) => {
        const len = (slice.count / total) * circumference;
        const node = (
          <circle
            key={slice.key}
            className={`ops-teacher-arc${
              slice.key === activeKey ? " is-active" : ""
            }`}
            cx={cx}
            cy={cy}
            r={r}
            fill="none"
            stroke={slice.color}
            strokeWidth={stroke}
            strokeDasharray={`${len} ${Math.max(0, circumference - len)}`}
            strokeDashoffset={-dashOffset}
            strokeLinecap="butt"
            transform={`rotate(-90 ${cx} ${cy})`}
            onMouseEnter={() => setActiveKey(slice.key)}
            onMouseLeave={() => setActiveKey(null)}
          />
        );
        dashOffset += len;
        return node;
      })
    );

  return (
    <div
      className="ops-mix-pie"
      role="img"
      aria-label="Topics by lecture, notes, and worksheet combination"
    >
      <div className={`ops-mix-pie-viz${linked ? " is-linked" : ""}`}>
        <svg
          viewBox={`0 0 ${size} ${size}`}
          width={size}
          height={size}
          className="ops-mix-pie-svg"
          preserveAspectRatio="xMidYMid meet"
        >
          {rings}
        </svg>
        <div className="ops-mix-pie-center">
          <strong>{formatCompact(total)}</strong>
          <span>topics</span>
        </div>
      </div>
      <div className="ops-mix-pie-key">
        {slices.map((slice) => (
          <div
            key={slice.key}
            className={`ops-mix-pie-row${
              slice.key === activeKey ? " is-active" : ""
            }`}
            onMouseEnter={() => setActiveKey(slice.key)}
            onMouseLeave={() => setActiveKey(null)}
          >
            <span
              className="ops-mix-pie-swatch"
              style={{ background: slice.color }}
            />
            <span className="ops-mix-pie-label">{slice.label}</span>
            <span className="ops-mix-pie-count">{slice.count}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AdminDashboard({
  adminName = "",
  modules = [],
  onLogout,
}) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError("");
      try {
        const { data } = await dashboardApi.getSummary();
        if (!cancelled) setSummary(data || {});
      } catch (err) {
        console.error("Dashboard summary failed:", err);
        if (!cancelled) {
          setError("Couldn’t load dashboard summary. Try a full refresh.");
          setSummary(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const kpis = useMemo(() => buildKpis(summary), [summary]);
  const queue = useMemo(() => buildActionQueue(summary), [summary]);
  const briefing = useMemo(() => buildBriefing(summary), [summary]);

  const logoutButton = onLogout ? (
    <button
      type="button"
      className="logout-button ops-sidebar-logout"
      onClick={onLogout}
      aria-label="Logout"
    >
      <i className="fa-solid fa-right-from-bracket" aria-hidden="true" />
      Logout
    </button>
  ) : null;

  const moduleNav = (
    <nav className="ops-module-nav" aria-label="Admin modules">
      <div className="ops-module-nav-label">Modules</div>
      <div className="ops-module-nav-list">
        {modules.map((option) => (
          <Link key={option.path} to={option.path} className="ops-module-link">
            <span
              className="ops-module-dot"
              style={{ background: option.color }}
            >
              <i className={option.icon} />
            </span>
            <span className="ops-module-name">{option.name}</span>
          </Link>
        ))}
      </div>
    </nav>
  );

  return (
    <div className="ops-layout">
      <aside className="ops-sidebar">
        <div className="ops-sidebar-scroll">{moduleNav}</div>
        {logoutButton ? (
          <div className="ops-sidebar-footer">{logoutButton}</div>
        ) : null}
      </aside>

      <div className="ops-main">
        <div className="ops-mobile-nav">
          {moduleNav}
          {logoutButton}
        </div>

        {loading ? (
          <div className="ops-loading">Brewing your dashboard…</div>
        ) : error ? (
          <div className="ops-error">{error}</div>
        ) : (
          <div className="ops-dash">
            <header className="ops-hero ops-dash-hero">
              <div className="ops-hero-left">
                <h1>{hourGreeting(adminName)}</h1>
                <p className="ops-reflect">{reflectiveLine()}</p>
              </div>
              <div className="ops-hero-right">
                <span className="ops-briefing-label">What’s waiting</span>
                <p className="ops-briefing">{briefing}</p>
              </div>
            </header>

            <div className="ops-kpi-shelf ops-dash-kpis">
              <div className="ops-kpi-grid">
                {kpis.map((kpi) =>
                  kpi.empty ? (
                    <div
                      key={kpi.key}
                      className="ops-kpi ops-kpi--empty"
                      aria-hidden="true"
                    />
                  ) : (
                    <Link key={kpi.key} to={kpi.to} className="ops-kpi">
                      <strong className="ops-kpi-value">{kpi.value}</strong>
                      <span className="ops-kpi-rule" aria-hidden="true" />
                      <span className="ops-kpi-label">{kpi.label}</span>
                    </Link>
                  ),
                )}
              </div>
            </div>

            <section className="ops-panel ops-dash-queue">
              <div className="ops-panel-head">
                <h2>Priority action queue</h2>
                <span>
                  {queue.length
                    ? `${queue.length} item${queue.length === 1 ? "" : "s"}`
                    : "Clear"}
                </span>
              </div>
              {queue.length === 0 ? (
                <p className="ops-queue-empty">
                  All clear — nothing needs your attention right now.
                </p>
              ) : (
                <ul className="ops-queue">
                  {queue.slice(0, 5).map((item) => (
                    <li key={item.key}>
                      <Link to={item.to} className="ops-queue-item">
                        <span>{item.title}</span>
                        <strong>{item.count}</strong>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {summary?.teachers || summary?.courses ? (
              <section className="ops-panel ops-dash-enroll">
                <div className="ops-enroll-stack">
                  {summary?.teachers ? (
                    <TeacherEnrollmentPie
                      teachers={summary.teachers.byStudents}
                      schedule={summary?.courses?.schedule}
                    />
                  ) : (
                    <p className="ops-muted">Teacher view not available.</p>
                  )}
                  <div className="ops-enroll-bottom">
                    <div className="ops-enroll-block">
                      <p className="ops-section-label">Top 5 courses</p>
                      {summary?.courses ? (
                        <TopLoadList
                          emptyText="No active courses yet."
                          items={(
                            summary.courses.topByStudents ||
                            summary.courses.seatsByCourse ||
                            []
                          ).map((course) => ({
                            id: course.courseId,
                            name: course.title,
                            students: course.students,
                          }))}
                        />
                      ) : (
                        <p className="ops-muted">Course view not available.</p>
                      )}
                    </div>
                    <div className="ops-enroll-block">
                      <p className="ops-section-label">Top 5 teachers</p>
                      {summary?.teachers ? (
                        <TopLoadList
                          emptyText="No teachers yet."
                          items={(
                            summary.teachers.topByStudents ||
                            summary.teachers.byStudents ||
                            []
                          ).map((teacher) => ({
                            id: teacher.teacherId,
                            name: teacher.name,
                            students: teacher.students,
                          }))}
                        />
                      ) : (
                        <p className="ops-muted">Teacher view not available.</p>
                      )}
                    </div>
                  </div>
                </div>
              </section>
            ) : (
              <section
                className="ops-panel ops-dash-enroll ops-panel--empty"
                aria-hidden="true"
              />
            )}

            <div className="ops-dash-right">
              {summary?.registrations ? (
                <section className="ops-panel ops-dash-enrollments">
                  <div className="ops-panel-head">
                    <h2>Enrollments</h2>
                    <span>Last 30 days</span>
                  </div>
                  <MiniBars
                    series={summary.registrations.enrollmentsByDay}
                    variant="enrollments"
                    days={30}
                    unitLabel="enrollments"
                  />
                </section>
              ) : (
                <section
                  className="ops-panel ops-dash-enrollments ops-panel--empty"
                  aria-hidden="true"
                />
              )}

              {summary?.cashLedger ? (
                <section className="ops-panel ops-dash-collections">
                  <div className="ops-panel-head">
                    <h2>Collections</h2>
                    <span>Last 30 days</span>
                  </div>
                  <MiniBars
                    series={summary.cashLedger.collectionsByDay}
                    variant="cash"
                    valueFormatter={formatPkr}
                    days={30}
                    unitLabel="collected"
                  />
                </section>
              ) : (
                <section
                  className="ops-panel ops-dash-collections ops-panel--empty"
                  aria-hidden="true"
                />
              )}

              <section className="ops-panel ops-dash-free">
                <div className="ops-panel-head">
                  <h2>Free resource</h2>
                  <span>Library & engagement</span>
                </div>
                <div className="ops-free-grid">
                  <div className="ops-free-col">
                    <p className="ops-section-label">Content library</p>
                    {summary?.content ? (
                      <div className="ops-library-body">
                        {summary.content.lectureCount != null ||
                        summary.content.noteCount != null ||
                        summary.content.worksheetCount != null ? (
                          <div className="ops-asset-grid">
                            {summary.content.lectureCount != null ? (
                              <Link
                                to="/admin/lectures"
                                className="ops-asset-chip"
                              >
                                <strong>
                                  {formatCompact(summary.content.lectureCount)}
                                </strong>
                                <span>lectures</span>
                              </Link>
                            ) : null}
                            {summary.content.noteCount != null ? (
                              <Link to="/admin/notes" className="ops-asset-chip">
                                <strong>
                                  {formatCompact(summary.content.noteCount)}
                                </strong>
                                <span>notes</span>
                              </Link>
                            ) : null}
                            {summary.content.worksheetCount != null ? (
                              <Link
                                to="/admin/worksheets"
                                className="ops-asset-chip"
                              >
                                <strong>
                                  {formatCompact(
                                    summary.content.worksheetCount,
                                  )}
                                </strong>
                                <span>worksheets</span>
                              </Link>
                            ) : null}
                          </div>
                        ) : (
                          <p className="ops-muted">
                            No content modules available.
                          </p>
                        )}

                        {summary.content.topicContentMix?.length ? (
                          <div className="ops-library-mix">
                            <p className="ops-engage-sub">Coverage mix</p>
                            <ContentMixPie
                              mix={summary.content.topicContentMix}
                              totalTopics={summary.content.topicsTotal}
                            />
                          </div>
                        ) : null}
                      </div>
                    ) : (
                      <p className="ops-muted">Content library not available.</p>
                    )}
                  </div>

                  <div className="ops-free-col">
                    <p className="ops-section-label">Student engagement</p>
                    {summary?.content ? (
                      <>
                        <p className="ops-engage-summary">
                          <strong>
                            {formatCompact(
                              summary.content.studentsExploring || 0,
                            )}
                          </strong>{" "}
                          students active ·{" "}
                          <strong>
                            {formatCompact(summary.content.topicsExplored || 0)}
                          </strong>
                          /
                          {formatCompact(summary.content.topicsTotal || 0)}{" "}
                          topics touched
                        </p>
                        <div className="ops-engage-lists">
                          <div>
                            <p className="ops-engage-sub">Top subjects</p>
                            {(summary.content.topSubjects || []).length ? (
                              <ul className="ops-rank-list">
                                {summary.content.topSubjects.map((subject) => {
                                  const n = Number(subject.students) || 0;
                                  return (
                                    <li key={subject.subjectId}>
                                      <span>{subject.name}</span>
                                      <strong>
                                        {n} student{n === 1 ? "" : "s"}
                                      </strong>
                                    </li>
                                  );
                                })}
                              </ul>
                            ) : (
                              <p className="ops-muted">No progress yet.</p>
                            )}
                          </div>
                          <div>
                            <p className="ops-engage-sub">Top topics</p>
                            {(summary.content.topTopics || []).length ? (
                              <ul className="ops-rank-list">
                                {summary.content.topTopics.map((topic) => {
                                  const n = Number(topic.students) || 0;
                                  return (
                                    <li key={topic.topicId}>
                                      <span>{topic.name}</span>
                                      <strong>
                                        {n} student{n === 1 ? "" : "s"}
                                      </strong>
                                    </li>
                                  );
                                })}
                              </ul>
                            ) : (
                              <p className="ops-muted">No progress yet.</p>
                            )}
                          </div>
                        </div>
                      </>
                    ) : (
                      <p className="ops-muted">Engagement not available.</p>
                    )}
                  </div>
                </div>
              </section>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
